<!--
  Summary:
  Root component of the web Vue application shell. Hosts router-view and global
  chrome (language selector, theme toggle, Sonner toaster) under one Professional
  visual language.

  Responsibilities / Role:
  - Anchor Vue Router via router-view for all web routes.
  - Show LanguageSelector on login / create-agency; ThemeToggle on public auth paths.
  - Mount the Sonner Toaster for useFeedback / showToast notifications.
  - Provide residual global styles (calendar schedule-x overrides, role/status badges,
    error/success message animations) using semantic design tokens.

  Out of Scope:
  - Route definitions and guards (web router).
  - Authentication / session bootstrap (main.ts, UseAuth, router guards).
  - Role-specific layout chrome (M_LoggedIn / B_LoggedIn / A_LoggedIn / RoleTaskbar).
  - Defining CSS token values (tokens.css).

  Dependencies:
  - vue-router (useRoute), vue (computed)
  - LanguageSelector, ThemeToggle, Toaster (sonner)
  - docs/design/TONE.md
-->
<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import LanguageSelector from '@/views/components/LanguageSelector.vue'
import { Toaster } from '@/components/ui/sonner'
import ThemeToggle from '@/components/ThemeToggle.vue'

const route = useRoute()

/** Show the language selector on login and create-agency. Admin settings hosts its own copy. */
const showLanguageSelector = computed(() => {
  const path = route.path
  return path === '/Login' || path === '/create-agency'
})

const showPublicThemeToggle = computed(() => {
  const path = route.path
  return path === '/Login' || path === '/login' || path === '/create-agency' || path === '/accept-invite' || path === '/accept-invitation'
})
</script>

<template>
  <div>
    <router-view />
    <LanguageSelector v-if="showLanguageSelector" :show-button="true" />
    <ThemeToggle v-if="showPublicThemeToggle" floating />
    <Toaster position="bottom-right" richColors />
  </div>
</template>

<style>
html,
body,
#app {
  margin: 0;
  padding: 0;
  min-height: 100%;
  background: var(--bg-page);
}

/* schedule-x calendar row height override */
.sx__time-grid .sx__time-grid__row {
  height: 24px !important;
  min-height: 24px !important;
}

.sx__time-grid-time {
  font-size: 0.7rem !important;
}

/* Style global pour tous les messages d'erreur - uniformisé */
.error-message {
  margin-top: var(--spacing-4);
  padding: var(--spacing-4);
  background: var(--status-error-bg);
  border: 1px solid var(--status-error-border);
  border-radius: var(--radius-lg);
  color: var(--status-error-text);
  animation: slideInFromTop var(--duration-base) var(--ease-default);
  display: flex;
  justify-content: space-between;
  align-items: center;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  box-sizing: border-box;
}

/* Style global pour tous les messages de confirmation - uniformisé */
.success-message {
  margin-top: var(--spacing-4);
  padding: var(--spacing-4);
  background: var(--status-success-bg);
  border: 1px solid var(--status-success-border);
  border-radius: var(--radius-lg);
  color: var(--status-success-text);
  animation: slideInFromTop var(--duration-base) var(--ease-default);
}

/* Animation pour les messages */
@keyframes slideInFromTop {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUpFromBottom {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* Style global pour les badges de rôle - uniformisé */
.role-badge {
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-xl);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border: 1px solid transparent;
  display: inline-block;
}

.role-badge.booker {
  background: var(--role-booker-bg);
  color: var(--role-booker-text);
  border-color: var(--role-booker-border);
}

.role-badge.model {
  background: var(--role-model-bg);
  color: var(--role-model-text);
  border-color: var(--role-model-border);
}

.role-badge.prospect {
  background: var(--bg-interactive);
  color: var(--text-secondary);
  border-color: var(--border-subtle);
}

.role-badge.agency_admin,
.role-badge.agency-admin {
  background: var(--role-admin-bg);
  color: var(--role-admin-text);
  border-color: var(--role-admin-border);
}

/* Style global pour les badges de statut - uniformisé */
.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-xl);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border: 1px solid transparent;
  display: inline-block;
}

.status-badge.pending {
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
  border-color: var(--status-warning-border);
}

.status-badge.cancelled {
  background: var(--status-error-bg);
  color: var(--status-error-text);
  border-color: var(--status-error-border);
}

.status-badge.accepted {
  background: var(--status-success-bg);
  color: var(--status-success-text);
  border-color: var(--status-success-border);
}

.status-badge.active {
  background: var(--status-success-bg);
  color: var(--status-success-text);
  border: 1px solid var(--status-success-border);
}

.status-badge.suspended {
  background: var(--status-error-bg);
  color: var(--status-error-text);
  border: 1px solid var(--status-error-border);
}

.status-badge.expired {
  background: var(--status-error-bg);
  color: var(--status-error-text);
  border-color: var(--status-error-border);
}

/* au-dessus de la taskbar (Radix/Teleport: force un z-index énorme) */

[data-radix-popper-content-wrapper]{
  z-index: 20000 !important;
}

/* au cas où tu veux cibler ta classe */
.dropdown-above{
  z-index: 20000 !important;
}
</style>
